import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import ProductCard from './ProductCard';
import SEOHead from './SEOHead';
import { createOrGetProductChat } from './chat/utils/chat';
import useAuthStore from '../store/authStore';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import ProductViewSkeleton from './ProductViewSkeleton';
import SellerDetailsSkeleton from './SellerDetailsSkeleton';
import ProductCardSkeleton from './ProductCardSkeleton';
import ProductViewHeaderSkeleton from './ProductViewHeaderSkeleton';
import { apiGet, getImageUrl } from '../utils/api';
import { useDarkMode } from '../contexts/DarkModeContext';
import ChatAppPopup from './chat/components/ChatAppPopup';
import useUserOnlineStatus from './chat/hooks/useUserOnlineStatus';
import ProductViewFooterBar from './ProductViewFooterBar';

// WhatsApp-style last seen formatter
const formatLastSeen = (lastSeenTimestamp) => {
  if (!lastSeenTimestamp) return 'Offline';
  
  const now = Date.now();
  const diff = now - lastSeenTimestamp;
  
  // Less than 1 minute
  if (diff < 60000) {
    return 'last seen just now';
  }
  
  // Less than 1 hour
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `last seen ${minutes} min ago`;
  }
  
  // Less than 24 hours
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return `last seen ${hours} hrs ago`;
  }
  
  // 24 hours or more - show date and time
  const date = new Date(lastSeenTimestamp);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
  
  return `last seen ${month} ${day}, ${year} • ${time}`;
};

// Add the paymentIcons array from Footer for reuse
const paymentIconsFooter = [
  { label: 'Visa', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><text x="24" y="21" textAnchor="middle" fontSize="14" fill="#1a237e" fontWeight="bold">VISA</text></svg>) },
  { label: 'MasterCard', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><circle cx="18" cy="16" r="8" fill="#ff9800"/><circle cx="30" cy="16" r="8" fill="#e53935" fillOpacity=".8"/></svg>) },
  { label: 'PayPal', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><text x="24" y="21" textAnchor="middle" fontSize="14" fill="#1565c0" fontWeight="bold">PayPal</text></svg>) },
  { label: 'Wise', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><text x="24" y="21" textAnchor="middle" fontSize="14" fill="#00bfae" fontWeight="bold">WISE</text></svg>) },
  { label: 'Bitcoin', svg: (<svg className="w-14 h-8" viewBox="0 0 48 32"><rect width="48" height="32" rx="6" fill="#fff"/><circle cx="24" cy="16" r="8" fill="#ffb300"/><text x="24" y="21" textAnchor="middle" fontSize="14" fill="#fff" fontWeight="bold">₿</text></svg>) },
];

// Mock related products (same category, 24 items)
const relatedProducts = Array.from({ length: 24 }).map((_, i) => ({
  image: `https://randomuser.me/api/portraits/women/${32 + i}.jpg`,
  platform: 'instagram',
  title: `${10 + i}K Followers Meow | Cats | Pets Insta Account ${i + 1}`,
  category: 'Pets & Animals',
  followers: 10000 + i * 1000,
  monetized: i % 2 === 0,
  oldPrice: 200 + i * 10,
  price: 150 + i * 8,
  onSale: i % 2 === 1,
}));

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: window?.location?.pathname || '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

function ProductView() {
  const { slug } = useParams();
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedProductsLoading, setRelatedProductsLoading] = useState(false);
  const [relatedPage, setRelatedPage] = useState(0);
  const PRODUCTS_PER_PAGE = 8; // 2 rows of 4 products on desktop, 4 rows of 2 products on mobile
  const totalPages = Math.ceil(relatedProducts.length / PRODUCTS_PER_PAGE);
  const { user } = useAuthStore();
  const [contactLoading, setContactLoading] = useState(false);
  const [sellerDetails, setSellerDetails] = useState(null);
  const [sellerUser, setSellerUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const [sliderIndex, setSliderIndex] = useState(0);
  const isMobileCategory = (product?.accountType === 'tiktok' || product?.accountType === 'instagram');
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const [sellerReviewStats, setSellerReviewStats] = useState({ averageRating: 0, totalReviews: 0 });
  
  // Chat popup state
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  // Dispatch chat state events for MobileFooterBar
  useEffect(() => {
    if (isChatOpen) {
      window.dispatchEvent(new CustomEvent('chatOpened'));
    } else {
      window.dispatchEvent(new CustomEvent('chatClosed'));
    }
  }, [isChatOpen]);
  
  // Verification popup state
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);

  // Get seller online status - moved to top level to avoid hooks order issues
  const sellerIdentifier = product?.userId ? 
    (product.userId.uid && product.userId.email ? 
      { uid: product.userId.uid || product.userId.firebaseUid, email: product.userId.email } : 
      (product.userId.uid || product.userId.firebaseUid || product.userId.email || null)
    ) : null;
  const { online: sellerOnline, lastSeen: sellerLastSeen } = useUserOnlineStatus(sellerIdentifier);

  // Logic for description truncation
  const description = product?.description || '';
  const isLongDescription = description.length > 250;
  const truncatedDescription = isLongDescription ? `${description.substring(0, 250)}...` : description;

  // Map backend fields to UI fields (move this up so useMemo can use them)
  const mainImage = product?.mainImage ? getImageUrl(product.mainImage) : '';
  const gallery = Array.isArray(product?.galleryImages)
    ? product.galleryImages.map(img => getImageUrl(img))
    : [];

  // useMemo for allImages at the top level
  const allImages = useMemo(() => {
    const imgs = [];
    if (mainImage) imgs.push(mainImage);
    if (Array.isArray(gallery)) imgs.push(...gallery);
    return imgs;
  }, [mainImage, gallery]);

  // Sanitize image sources for UI rendering
  const sanitizeImageSrc = (url) => {
    if (!url || typeof url !== 'string') return '';
    try {
      // Allow absolute http/https URLs
      const u = new URL(url, window.location.origin);
      if (u.protocol === 'http:' || u.protocol === 'https:') return u.href;
    } catch (_) {
      // Not a full URL – allow only safe app-relative paths like /uploads/...
      if (url.startsWith('/uploads/')) {
        return url.replace(/[^\w\-./]/g, '');
      }
    }
    return '';
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setLoading(true);
    // Ensure related section shows skeleton while we resolve product and start related fetch
    setRelatedProductsLoading(true);

    // 5-minute TTL
    const TTL_MS = 5 * 60 * 1000;
    const cacheKey = `product_details_${slug}`;
    const cacheTimeKey = `product_details_time_${slug}`;

            try {
          const cached = sessionStorage.getItem(cacheKey);
          const cachedTime = sessionStorage.getItem(cacheTimeKey);
          if (cached && cachedTime && (Date.now() - parseInt(cachedTime, 10)) < TTL_MS) {
            let data;
            try {
              data = JSON.parse(cached);
            } catch (parseError) {
              safeReportError('Failed to parse cached product data:', parseError);
              data = null;
            }
            if (data) {
              setProduct(data);
              setLoading(false);
              // Still fetch fresh in background (optional)
            }
          }
        } catch (error) {
          safeReportError('session_storage_access_failed');
        }

    // Intelligently detect if slug is an ObjectId or a slug
    const isObjectId = /^[a-f\d]{24}$/i.test(slug);
    // Choose the appropriate API endpoint
    const apiUrl = isObjectId
      ? `/api/products/${slug}`
      : `/api/products/slug/${slug}`;

    apiGet(`${apiUrl}?t=${Date.now()}`, { signal })
      .then(res => {
        if (!res.ok) throw new Error('Product not found');
        return res.json();
      })
      .then(data => {
        setProduct(data);
        setLoading(false);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(data));
          sessionStorage.setItem(cacheTimeKey, Date.now().toString());
        } catch {}

        // Fetch related products by accountType, excluding current product
        if (data.accountType) {
          setRelatedProductsLoading(true);
          const relKey = `related_products_${data.accountType}`;
          const relTimeKey = `related_products_time_${data.accountType}`;
          const maybeServeRelatedFromCache = () => {
            try {
              const c = sessionStorage.getItem(relKey);
              const ct = sessionStorage.getItem(relTimeKey);
              if (c && ct && (Date.now() - parseInt(ct, 10)) < TTL_MS) {
                let list;
                try {
                  list = JSON.parse(c);
                } catch (parseError) {
                  safeReportError('related_products_cache_parse_failed');
                  list = null;
                }
                if (list) {
                  setRelatedProducts(Array.isArray(list) ? list.filter(p => p._id !== data._id) : []);
                  setRelatedProductsLoading(false);
                  return true;
                }
              }
            } catch (error) {
              safeReportError('related_products_cache_access_failed');
            }
            return false;
          };

          if (!maybeServeRelatedFromCache()) {
            apiGet(`/api/products?accountType=${encodeURIComponent(data.accountType)}`, { signal })
              .then(res => res.json())
              .then(list => {
                const cleaned = Array.isArray(list) ? list.filter(p => p && p._id !== data._id) : [];
                setRelatedProducts(cleaned);
                try {
                  sessionStorage.setItem(relKey, JSON.stringify(cleaned));
                  sessionStorage.setItem(relTimeKey, Date.now().toString());
                } catch {}
                setRelatedProductsLoading(false);
              })
              .catch(err => {
                // Only log if not aborted (expected behavior)
                if (!signal.aborted) {
                  safeReportError('related_products_fetch_failed', { error: err.message });
                }
                setRelatedProducts([]);
                setRelatedProductsLoading(false);
              });
          }
        }
      })
      .catch(err => {
        // Only handle error if not aborted (expected behavior)
        if (signal.aborted) {
          // Silently ignore aborted requests - this is expected when navigating away
          return;
        }
        setError('Failed to load product.');
        setLoading(false);
        // Stop related skeleton on product load failure
        setRelatedProductsLoading(false);
      });

    return () => controller.abort();
  }, [slug]);

  useEffect(() => {
    async function fetchSellerDetails() {
      if (product && product.userId) {

        
        // If userId is already populated (object), use it directly
        if (typeof product.userId === 'object' && product.userId.username) {

          setSellerDetails({
            username: product.userId.username,
            avatar: product.userId.avatar,
            verified: product.userId.verified,
            rating: product.userId.rating,
            reviews: product.userId.reviews,
            joined: product.userId.joined || product.userId.createdAt,
            deals: (typeof product.userId.deals !== 'undefined' ? product.userId.deals : product.userId.transactions),
            total: (typeof product.userId.total !== 'undefined' ? product.userId.total : product.userId.totalAmount),
            email: product.userId.email,
            uid: product.userId.uid
          });
          setSellerUser(product.userId);
          // If joined is missing, try fetching public user to get createdAt
          try {
            if (product.userId.username) {
              const res = await apiGet(`/api/auth/users/public/${encodeURIComponent(product.userId.username)}`);
              if (res.ok) {
                const publicUser = await res.json();
                if (publicUser) {
                  setSellerUser(prev => ({ ...(prev || {}), createdAt: publicUser.createdAt, transactions: publicUser.transactions, totalAmount: publicUser.totalAmount }));
                  setSellerDetails(prev => prev ? ({ 
                    ...prev, 
                    joined: prev.joined || publicUser.createdAt,
                    deals: typeof prev.deals !== 'undefined' ? prev.deals : publicUser.transactions,
                    total: typeof prev.total !== 'undefined' ? prev.total : publicUser.totalAmount,
                  }) : prev);
                }
              }
            }
          } catch (_) {}
          try {
            const sellerIdForReviews = product.userId.uid || product.userId.firebaseUid;
            if (sellerIdForReviews) {
              const res = await apiGet(`/api/reviews/seller/${sellerIdForReviews}?limit=1`);
              if (res.ok) {
                const data = await res.json();
                setSellerReviewStats(data.stats || { averageRating: 0, totalReviews: 0 });
              }
            }
          } catch (_) {}
          return;
        }
        
        // Fallback: If userId is a string (MongoDB ID), fetch user data
        if (typeof product.userId === 'string') {
          try {
            const res = await apiGet(`/api/users/public/id/${product.userId}`);
            if (!res.ok) throw new Error('Failed to fetch seller user from backend');
            const sellerUser = await res.json();

            
            // Set seller details from backend data
            setSellerDetails({
              username: sellerUser.username,
              avatar: sellerUser.avatar,
              verified: false, // Default value since not in backend
              rating: 5, // Default value since not in backend
              reviews: 0, // Default value since not in backend
              joined: sellerUser.createdAt,
              deals: sellerUser.transactions || 0,
              total: sellerUser.totalAmount || 0,
              email: sellerUser.email,
              uid: sellerUser.firebaseUid
            });
            setSellerUser(sellerUser);
            try {
              if (sellerUser.firebaseUid) {
                const res2 = await apiGet(`/api/reviews/seller/${sellerUser.firebaseUid}?limit=1`);
                if (res2.ok) {
                  const data2 = await res2.json();
                  setSellerReviewStats(data2.stats || { averageRating: 0, totalReviews: 0 });
                }
              }
            } catch (_) {}
          } catch (err) {
            setSellerDetails(null);
            safeReportError('seller_fetch_failed');
          }
        }
      }
    }
    fetchSellerDetails();
  }, [product]);

  useEffect(() => {
    setSliderIndex(0);
  }, [product?._id]);

  useEffect(() => {
    // Check if this product is already in wishlist
    let wishlist = [];
    try {
      wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    } catch (error) {
      safeReportError('wishlist_parse_failed');
      wishlist = [];
    }
    setIsWishlisted(Array.isArray(wishlist) && wishlist.some(item => item._id === product?._id));
    
    // Check if this product is already in cart
    let cart = [];
    try {
      cart = JSON.parse(localStorage.getItem('cart') || '[]');
    } catch (error) {
      safeReportError('cart_parse_failed');
      cart = [];
    }
    setIsInCart(Array.isArray(cart) && cart.some(item => item._id === product?._id));
  }, [product?._id]);

  // Listen for localStorage changes to update cart and wishlist state
  useEffect(() => {
    const handleStorageChange = () => {
      if (!product?._id) return;
      
      // Check wishlist
      let wishlist = [];
      try {
        wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
      } catch (error) {
        safeReportError('wishlist_parse_failed');
        wishlist = [];
      }
      setIsWishlisted(Array.isArray(wishlist) && wishlist.some(item => item._id === product._id));
      
      // Check cart
      let cart = [];
      try {
        cart = JSON.parse(localStorage.getItem('cart') || '[]');
      } catch (error) {
        safeReportError('cart_parse_failed');
        cart = [];
      }
      setIsInCart(Array.isArray(cart) && cart.some(item => item._id === product._id));
    };

    // Listen for storage events (when localStorage changes in other tabs)
    window.addEventListener('storage', handleStorageChange);
    
    // Listen for custom cart update events (same tab)
    window.addEventListener('cartUpdated', handleStorageChange);
    
    // Listen for custom wishlist update events (same tab)
    window.addEventListener('wishlistUpdated', handleStorageChange);
    
    // Also check on mount and when product changes
    handleStorageChange();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cartUpdated', handleStorageChange);
      window.removeEventListener('wishlistUpdated', handleStorageChange);
    };
  }, [product?._id]);

  if (loading) {
    return (
      <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
        isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
      }`}>
        <Header />
        <ProductViewHeaderSkeleton />
        <div className="flex flex-col items-center py-4 sm:py-6 md:py-8 px-4 sm:px-6">
          <div className="w-full max-w-[1495px] mx-auto flex flex-col xl:flex-row gap-8">
            <ProductViewSkeleton />
            <SellerDetailsSkeleton />
          </div>
        </div>
      </div>
    );
  }
  if (error || !product) {
    return <div className={`min-h-screen flex items-center justify-center text-xl transition-colors duration-300 ${
      isDarkMode ? 'text-red-400' : 'text-red-600'
    }`}>{error || 'Product not found.'}</div>;
  }

  // Map backend fields to UI fields
  const stats = product.stats || {};

  // Use populated userId for seller details
  const seller = product.userId ? {
    uid: product.userId.uid || product.userId.firebaseUid || '',
    email: product.userId.email || '',
    name: product.userId.username || product.userId.name || 'Unknown Seller',
    avatar: product.userId.avatar || '/images/default-avatar.png',
    username: product.userId.username || 'Unknown Seller',
    verified: product.userId.verified || false,
    rating: product.userId.rating || 5,
    reviews: product.userId.reviews || 0,
    joined: product.userId.joined || sellerUser?.createdAt || product.userId.createdAt || 'N/A',
    // Prefer live public profile numbers when available
    deals: (sellerUser?.transactions ?? (typeof product.userId.transactions !== 'undefined' ? product.userId.transactions : product.userId.deals)) || 0,
    total: (sellerUser?.totalAmount ?? (typeof product.userId.totalAmount !== 'undefined' ? product.userId.totalAmount : product.userId.total)) || 0,
  } : {
    uid: '',
    email: '',
    name: 'Unknown Seller',
    avatar: '/images/default-avatar.png',
    username: 'Unknown Seller',
    verified: false,
    rating: 5,
    reviews: 0,
    joined: 'N/A',
    deals: 0,
    total: 0,
  };


  const handleContactSeller = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (!product.userId) {
      toast.error('Seller details not found. Please try again later.');
      return;
    }
    setContactLoading(true);
    try {
      const productData = {
        id: product._id || product.id || 'unknown',
        name: (() => {
          const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
          return safeTitle || 'Unknown Product';
        })(),
        image: product.mainImage?.startsWith('http')
          ? (() => {
              const safeImage = (product.mainImage || '').replace(/[<>\"'&]/g, '');
              return safeImage || '';
            })()
          : product.mainImage
            ? `/uploads/${product.mainImage?.replace(/[^a-zA-Z0-9._-]/g, '')}`
            : '',
        price: (() => {
          const safePrice = (product.salePrice || product.price || '').toString().replace(/[<>\"'&]/g, '');
          return safePrice || '';
        })(),
      };
      const buyerData = {
        uid: (() => {
          const safeUid = (user.firebaseUid || user.uid || '').replace(/[<>\"'&]/g, '');
          return safeUid || 'unknown';
        })(),
        email: (() => {
          const safeEmail = (user.email || '').replace(/[<>\"'&]/g, '');
          return safeEmail || 'unknown';
        })(),
        name: (() => {
          const safeName = (user.username || user.displayName || user.name || user.email || '').replace(/[<>\"'&]/g, '');
          return safeName || 'Unknown Buyer';
        })(),
      };
      // Use seller data directly from product.userId
      const sellerData = {
        uid: (() => {
          const safeUid = (product.userId.uid || product.userId.firebaseUid || '').replace(/[<>\"'&]/g, '');
          return safeUid || 'unknown';
        })(),
        email: (() => {
          const safeEmail = (product.userId.email || '').replace(/[<>\"'&]/g, '');
          return safeEmail || 'unknown';
        })(),
        name: (() => {
          const safeName = (product.userId.username || '').replace(/[<>\"'&]/g, '');
          return safeName || 'Unknown Seller';
        })(),
      };
      
      const conversationId = await createOrGetProductChat(productData, buyerData, sellerData);
      
      // Set the conversation ID and open chat popup instead of navigating
      setSelectedConversationId(conversationId);
      setIsChatOpen(true);
      setContactLoading(false);
      
      // Show success message
      toast.success('Chat opened! You can now message the seller.');
    } catch (err) {
      safeReportError('chat_create_failed');
      setContactLoading(false);
      toast.error('Failed to start chat. Please try again.');
    }
  };

  const openModal = (idx) => {
    setModalIndex(idx);
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);
  const prevImage = () => setModalIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  const nextImage = () => setModalIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));

  const categoryRoute = (() => {
    const type = (product?.accountType || '').toLowerCase();
    switch (type) {
      case 'youtube':
        return '/buy-youtube-channel';
      case 'tiktok':
        return '/buy-tiktok-account';
      case 'instagram':
        return '/buy-instagram-account';
      case 'facebook':
        return '/buy-facebook-page';
      default:
        return '/marketplace';
    }
  })();

  const handleSliderPrev = () => setSliderIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  const handleSliderNext = () => setSliderIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));

  const handleToggleWishlist = () => {
    if (!product) return;
    let wishlist = [];
    try {
      wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    } catch (error) {
      safeReportError('wishlist_parse_failed');
      wishlist = [];
    }
    
    if (!Array.isArray(wishlist)) {
      wishlist = [];
    }
    
    const index = wishlist.findIndex(item => item._id === product._id);
    if (index !== -1) {
      wishlist.splice(index, 1);
      try {
        localStorage.setItem('wishlist', JSON.stringify(wishlist));
        setIsWishlisted(false);
        toast.info('Removed from wishlist!');
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('wishlistUpdated', { detail: { wishlist } }));
      } catch (error) {
        safeReportError('wishlist_save_failed');
      }
    } else {
      wishlist.push(product);
      try {
        localStorage.setItem('wishlist', JSON.stringify(wishlist));
        setIsWishlisted(true);
        toast.success('Added to wishlist!');
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('wishlistUpdated', { detail: { wishlist } }));
      } catch (error) {
        safeReportError('wishlist_save_failed');
      }
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    let cart = [];
    try {
      cart = JSON.parse(localStorage.getItem('cart') || '[]');
    } catch (error) {
      safeReportError('cart_parse_failed');
      cart = [];
    }
    
    if (!Array.isArray(cart)) {
      cart = [];
    }
    
    if (cart.some(item => item._id === product._id)) {
      toast.info('Product is already in your cart!');
      return;
    }

    // Store only what the cart page actually uses
    const cartItem = {
      _id: product._id,
      title: product.title,
      price: product.price ?? 0,
      salePrice: product.salePrice ?? null,
      mainImage: product.mainImage || null,
      stats: { category: product?.stats?.category || product?.accountType || '' },
      accountType: product.accountType,
      slug: product.slug,
    };

    cart.push(cartItem);
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
      setIsInCart(true);
      toast.success('Added to cart!');
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { cart } }));
    } catch (error) {
      safeReportError('cart_save_failed');
    }
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
    }`}>
      <SEOHead
        title={product ? `${(() => {
          const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
          return safeTitle || 'Product Title';
        })()} | YTProud` : 'Product | YTProud'}
        description={product ? (() => {
          const safeDescription = (product.description || '').replace(/[<>\"'&]/g, '');
          const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
          const safeAccountType = (product.accountType || '').replace(/[<>\"'&]/g, '');
          return safeDescription || `Buy ${safeTitle} - ${safeAccountType} account with ${product.stats?.followers || 0} followers`;
        })() : 'Product details'}
        keywords={product ? `${(() => {
          const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
          return safeTitle || 'Product Title';
        })()}, ${(() => {
          const safeAccountType = (product.accountType || '').replace(/[<>\"'&]/g, '');
          return safeAccountType || 'Unknown';
        })()}, social media account, buy account, followers` : 'social media account'}
        canonical={`https://ytproud.com/product/${encodeURIComponent(product?.slug || slug)}`}
        ogTitle={product ? `${(() => {
          const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
          return safeTitle || 'Product Title';
        })()} | YTProud` : 'Product | YTProud'}
        ogDescription={product ? (() => {
          const safeDescription = (product.description || '').replace(/[<>\"'&]/g, '');
          const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
          const safeAccountType = (product.accountType || '').replace(/[<>\"'&]/g, '');
          return safeDescription || `Buy ${safeTitle} - ${safeAccountType} account`;
        })() : 'Product details'}
        ogImage={product?.mainImage ? (product.mainImage.startsWith('http') ? product.mainImage : `/uploads/${product.mainImage?.replace(/[^a-zA-Z0-9._-]/g, '')}`) : '/images/og-default.jpg'}
        twitterTitle={product ? `${(() => {
          const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
          return safeTitle || 'Product Title';
        })()} | YTProud` : 'Product | YTProud'}
        twitterDescription={product ? (() => {
          const safeDescription = (product.description || '').replace(/[<>\"'&]/g, '');
          const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
          const safeAccountType = (product.accountType || '').replace(/[<>\"'&]/g, '');
          return safeDescription || `Buy ${safeTitle} - ${safeAccountType} account`;
        })() : 'Product details'}
        twitterImage={product?.mainImage ? (product.mainImage.startsWith('http') ? product.mainImage : `/uploads/${product.mainImage?.replace(/[^a-zA-Z0-9._-]/g, '')}`) : '/images/og-default.jpg'}
        schemaJsonLd={product ? {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": (() => {
            const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
            return safeTitle || 'Product Title';
          })(),
          "description": (() => {
            const safeDescription = (product.description || '').replace(/[<>\"'&]/g, '');
            return safeDescription || 'Product Description';
          })(),
          "image": product.mainImage ? (product.mainImage.startsWith('http') ? product.mainImage : `/uploads/${product.mainImage?.replace(/[^a-zA-Z0-9._-]/g, '')}`) : '',
          "offers": {
            "@type": "Offer",
            "price": (() => {
              const safePrice = (product.price || 0).toString().replace(/[<>\"'&]/g, '');
              return safePrice || '0';
            })(),
            "priceCurrency": "USD",
            "availability": product.stockStatus === 'outofstock' ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"
          },
          "brand": {
            "@type": "Brand",
            "name": (() => {
              const safeAccountType = (product.accountType || '').replace(/[<>\"'&]/g, '');
              return safeAccountType || 'Unknown';
            })()
          }
        } : null}
      />
      <Header />
      {/* Modern Card-Style Header - Full Width */}
      <div className="relative z-10 mt-4 sm:mt-8">
        <div className="max-w-[1495px] w-full mx-auto px-3 sm:px-4">
          <div className={`rounded-xl sm:rounded-2xl shadow-lg px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 transition-all duration-500 ${
            isDarkMode 
              ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
              : 'bg-white'
          }`}>
            {/* Breadcrumbs - Mobile Optimized */}
            <div className={`flex items-center text-xs sm:text-sm gap-1 sm:gap-2 overflow-hidden transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-400'
            }`}>
              <Link to="/marketplace" className={`truncate transition-colors duration-300 ${
                isDarkMode ? 'hover:text-white' : 'hover:text-black'
              }`}>Marketplace</Link>
              <svg className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-600' : 'text-gray-300'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <Link to={categoryRoute} className={`truncate transition-colors duration-300 ${
                isDarkMode ? 'hover:text-white' : 'hover:text-black'
              }`}>
                {product.accountType === 'youtube' ? 'Youtube Channels' :
                 product.accountType ? product.accountType.charAt(0).toUpperCase() + product.accountType.slice(1) + ' Accounts' : ''}
              </Link>
              <svg className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-600' : 'text-gray-300'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className={`font-semibold truncate min-w-0 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>{(() => {
                const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
                return safeTitle || 'Product Title';
              })()}</span>
            </div>
            {/* Cart and Wishlist Icons - Mobile Optimized */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Cart Icon */}
              <button
                className={`inline-flex items-center justify-center p-2.5 sm:p-2 rounded-full shadow transition ${isInCart ? 'ring-2 ring-blue-400' : ''} ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
                title={isInCart ? 'In Cart' : 'Add to Cart'}
                onClick={handleAddToCart}
              >
                <svg className={`w-5 h-5 sm:w-6 sm:h-6 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`} fill={isInCart ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61l1.38-7.39H6" />
                </svg>
              </button>
              {/* Wishlist Icon */}
              <button
                className={`inline-flex items-center justify-center p-2.5 sm:p-2 rounded-full shadow transition ${isWishlisted ? 'ring-2 ring-red-400' : ''} ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
                title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
                onClick={handleToggleWishlist}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" fill={isWishlisted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M16.5 3c-1.74 0-3.41 1.01-4.5 2.09C10.91 4.01 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54a2 2 0 0 0 2.9 0C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Main content */}
      <div className="flex flex-col xl:flex-row gap-8 mx-auto py-8 px-4" style={{ maxWidth: '1495px' }}>
        {/* Product Box */}
        <div className={`flex-1 rounded-xl shadow-lg p-8 border-t-[6px] ${isDarkMode ? '' : 'border-red-500'} rounded-t-2xl transition-all duration-500 ${
          isDarkMode 
            ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50' 
            : 'bg-white'
        }`} style={{ borderTopColor: isDarkMode ? '#06B6D4' : undefined }}>
          <div className="flex flex-col md:flex-row gap-8">
            {/* Image & Gallery */}
            <div className="flex-1 w-full">
              {isMobileCategory ? (
                <div className="relative flex flex-col items-center">
                  <div
                    key={sliderIndex}
                    className="relative w-full aspect-[9/16] bg-black rounded-3xl overflow-hidden flex items-center justify-center shadow-lg cursor-pointer zoom-image-container"
                    onClick={() => openModal(sliderIndex)}
                    title="Click to zoom"
                  >
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={sliderIndex}
                        src={sanitizeImageSrc(allImages[sliderIndex])}
                        srcSet={() => {
                          const base = sanitizeImageSrc(allImages[sliderIndex]);
                          return base ? `${base}?w=480 480w, ${base}?w=720 720w, ${base}?w=1080 1080w` : '';
                        }}
                        sizes="(max-width: 640px) 90vw, (max-width: 1024px) 60vw, 480px"
                        width="480"
                        height="853"
                        loading="lazy"
                        alt="Product Mobile"
                        className="w-full h-auto object-cover zoom-image"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                      />
                    </AnimatePresence>
                    <span className="zoom-icon-overlay">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="20" cy="20" r="18" stroke="white" strokeWidth="2.5" fill="rgba(0,0,0,0.3)"/>
                        <path d="M20 14v12M14 20h12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                    </span>
                    {/* Left Arrow */}
                    <button
                      onClick={e => { e.stopPropagation(); handleSliderPrev(); }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-white shadow hover:bg-gray-200 transition border border-gray-200 z-10"
                      aria-label="Previous"
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <polyline points="14 5 8 11 14 17" stroke="#222" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                    {/* Right Arrow */}
                    <button
                      onClick={e => { e.stopPropagation(); handleSliderNext(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-white shadow hover:bg-gray-200 transition border border-gray-200 z-10"
                      aria-label="Next"
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <polyline points="8 5 14 11 8 17" stroke="#222" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      </svg>
                    </button>
                  </div>
                  {/* Dots Indicator - now inside the card, just below the frame */}
                  <div className="flex justify-center gap-2 mt-4">
                    {allImages.map((_, idx) => (
                      <span
                        key={idx}
                        onClick={() => setSliderIndex(idx)}
                        className={`w-3 h-3 rounded-full cursor-pointer ${sliderIndex === idx ? 'bg-red-500' : 'bg-gray-300'} transition`}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <>
              {mainImage && (
                    <a onClick={() => openModal(0)} className="cursor-pointer block zoom-image-container w-full">
                      <img
                        src={sanitizeImageSrc(mainImage)}
                        srcSet={() => {
                          const base = sanitizeImageSrc(mainImage);
                          return base ? `${base}?w=480 480w, ${base}?w=720 720w, ${base}?w=1080 1080w` : '';
                        }}
                        sizes="(max-width: 640px) 90vw, (max-width: 1024px) 60vw, 720px"
                        width="720"
                        height="480"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                        alt="Product"
                        className="zoom-image rounded-2xl border shadow-lg w-full h-auto max-h-[170px] object-cover mb-6 bg-white"
                      />
                      <span className="zoom-icon-overlay">
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="20" cy="20" r="18" stroke="white" strokeWidth="2.5" fill="rgba(0,0,0,0.3)"/>
                          <path d="M20 14v12M14 20h12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                        </svg>
                      </span>
              </a>
              )}
              {gallery.length > 0 && (
                <div>
                      <div className="font-semibold mb-3 text-lg">Attachments</div>
                      <div className="grid grid-cols-3 gap-4">
                    {gallery.map((img, i) => (
                          <a key={i} onClick={() => openModal(i + 1)} className="cursor-pointer block zoom-image-container">
                            <img
                              src={sanitizeImageSrc(img)}
                              srcSet={() => {
                                const base = sanitizeImageSrc(img);
                                return base ? `${base}?w=200 200w, ${base}?w=360 360w, ${base}?w=720 720w` : '';
                              }}
                              sizes="(max-width: 640px) 40vw, 200px"
                              width="200"
                              height="128"
                              loading="lazy"
                              alt="Gallery"
                              className="zoom-image w-[140px] h-[90px] object-cover rounded-2xl border shadow bg-white"
                            />
                            <span className="zoom-icon-overlay">
                              <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="20" cy="20" r="18" stroke="white" strokeWidth="2.5" fill="rgba(0,0,0,0.3)"/>
                                <path d="M20 14v12M14 20h12" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                              </svg>
                            </span>
                      </a>
                    ))}
                  </div>
                </div>
                  )}
                </>
              )}
            </div>
            {/* Product Info */}
            <div className="flex-[2] min-w-[300px] w-full">
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <h2 className={`product-title text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-0 break-words leading-tight transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{(() => {
                  const safeTitle = (product.title || '').replace(/[<>\"'&]/g, '');
                  return safeTitle || 'Product Title';
                })()}</h2>
                {(() => {
                  const safeUrl = (product.accountUrl || '').replace(/[<>\"'&]/g, '');
                  return safeUrl && safeUrl.startsWith('http') ? (
                    <a 
                      href={safeUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-md shadow-sm hover:shadow-md transform hover:scale-102 transition-all duration-300 text-xs sm:text-sm mt-1 sm:mt-0 group"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      <span>View Profile</span>
                      <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 transition-transform duration-300 group-hover:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </a>
                  ) : null;
                })()}
              </div>
              {/* Description with Read More/Less */}
              <div className={`product-short-description mb-4 text-sm sm:text-base leading-relaxed px-1 sm:px-0 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`} style={{ minHeight: '60px' }}>
                <div className="description-content" style={{ fontWeight: 'normal' }}>
                  {showFullDesc ? description : truncatedDescription}
                </div>
                {isLongDescription && (
                  <button className={`font-bold mt-2 text-xs sm:text-sm underline transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-black'
                  }`} onClick={() => setShowFullDesc(!showFullDesc)}>
                    {showFullDesc ? 'Read Less' : 'Read More'}
                  </button>
                )}
              </div>
              {/* Info Grid */}
              <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 rounded-xl p-3 sm:p-4 mb-5 sm:mb-6 text-center text-xs sm:text-sm transition-all duration-300 ${
                isDarkMode ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{(() => {
                  const safeFollowers = (stats.followers || '').toString().replace(/[<>\"'&]/g, '');
                  return safeFollowers || '-';
                })()}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Followers</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{(() => {
                  const safeMonetized = (stats.monetized || '').toString().replace(/[<>\"'&]/g, '');
                  return safeMonetized || '-';
                })()}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Monetized</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{(() => {
                  const safeCategory = (stats.category || product.accountType || '').replace(/[<>\"'&]/g, '');
                  return safeCategory || '-';
                })()}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Category</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{(() => {
                  const safeStrikes = (stats.strikes || '').toString().replace(/[<>\"'&]/g, '');
                  return safeStrikes || '-';
                })()}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Strikes</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{(() => {
                  const safeIncome = (stats.income || '').toString().replace(/[<>\"'&]/g, '');
                  return safeIncome || '-';
                })()}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Month Income</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{(() => {
                  const safeVideos = (stats.videos || '').toString().replace(/[<>\"'&]/g, '');
                  return safeVideos || '-';
                })()}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Videos</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{(() => {
                  const safeViews = (stats.views || '').toString().replace(/[<>\"'&]/g, '');
                  return safeViews || '-';
                })()}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Total Views</span></div>
                <div className="flex flex-col items-center"><span className={`font-bold text-base sm:text-lg transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-black'
                }`}>{(() => {
                  const safeCreated = (stats.created || '').replace(/[<>\"'&]/g, '');
                  return safeCreated || '-';
                })()}</span><span className={`text-[10px] sm:text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Created</span></div>
              </div>
              {/* Price, Buy Now, Payment, Delivery */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-8 mb-2 mt-6 sm:mt-8">
                {/* Left: Price, Buy Now */}
                <div className="flex flex-col items-start gap-2 flex-1 min-w-[160px] w-full">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className={`text-2xl sm:text-3xl font-extrabold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>USD ${(() => {
                      const safePrice = (product.salePrice ? product.salePrice : product.price || 0).toString().replace(/[<>\"'&]/g, '');
                      return safePrice || '0';
                    })()}</span>
                    {product.salePrice && <span className={`text-lg sm:text-xl line-through transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>${(() => {
                      const safePrice = (product.price || 0).toString().replace(/[<>\"'&]/g, '');
                      return safePrice || '0';
                    })()}</span>}
                  </div>
                  {(!('stockStatus' in product) || !('quantity' in product) || (product.stockStatus === 'instock' && product.quantity > 0)) ? (
                    user?.isBanned ? (
                      <button
                        className="mt-2 w-full sm:w-48 h-12 sm:h-14 bg-gray-500 text-white font-bold rounded-xl text-lg sm:text-xl shadow flex items-center justify-center cursor-not-allowed"
                        disabled
                      >
                        Buy Now
                      </button>
                    ) : (
                      <button
                        className="mt-2 w-full sm:w-48 h-12 sm:h-14 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-lg sm:text-xl shadow transition-all flex items-center justify-center"
                        onClick={() => navigate('/checkout', { state: { product } })}
                      >
                        Buy Now
                      </button>
                    )
                  ) : (
                    <button
                      className="mt-2 w-full sm:w-48 h-12 sm:h-14 bg-gray-500 text-white font-bold rounded-xl text-lg sm:text-xl shadow flex items-center justify-center cursor-not-allowed"
                      disabled
                    >
                      Out of Stock
                    </button>
                  )}
                </div>
                {/* Right: Delivery, Secure, Payment Icons */}
                <div className="flex flex-col md:items-end gap-2 flex-1 min-w-[160px] w-full mt-4 sm:mt-0">
                  <div className="flex flex-row gap-3 sm:gap-6 mb-2 md:justify-end flex-nowrap whitespace-nowrap">
                    <div className={`flex items-center gap-1 sm:gap-2 text-sm sm:text-base font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {/* Truck icon */}
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 17V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v12"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/><path d="M21 17V9a2 2 0 0 0-2-2h-4"/></svg>
                      <span className="whitespace-nowrap">Fast Delivery</span>
                    </div>
                    <div className={`flex items-center gap-1 sm:gap-2 text-sm sm:text-base font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {/* Shield icon */}
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      <span className="whitespace-nowrap">Secure Transaction</span>
                    </div>
                  </div>
                  <div className="flex flex-row gap-2 sm:gap-3 mt-2 payment-icons md:justify-end">
                    {paymentIconsFooter.map((icon, i) => (
                      <span key={i} title={icon.label} className="inline-flex items-center justify-center">{icon.svg}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Seller Box */}
        <div className={`w-full max-w-full xl:w-[340px] xl:max-w-[340px] rounded-2xl shadow-lg p-5 sm:p-7 flex-shrink-0 flex flex-col justify-between border-t-[6px] ${isDarkMode ? '' : 'border-red-500'} rounded-t-2xl mx-0 transition-all duration-500 ${
          isDarkMode 
            ? 'bg-[#2a2a2a] shadow-2xl shadow-black/50' 
            : 'bg-white'
        }`} style={{ borderTopColor: isDarkMode ? '#06B6D4' : undefined }}>
          <h3 className={`text-2xl font-bold mb-5 transition-colors duration-300 flex items-center gap-3 ${
            isDarkMode ? 'text-white' : 'text-black'
          }`}>
            <div className="w-1 h-8 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></div>
            <span className={`transition-colors duration-300 ${
              isDarkMode ? 'text-white' : 'text-black'
            }`}>Seller Details</span>
            <div className="flex-1 h-0.5 bg-gradient-to-r from-red-500/50 to-transparent rounded-full"></div>
          </h3>
          <div className="flex items-center gap-4 mb-4">
            <a href={`/profile/${encodeURIComponent(seller.username?.replace(/[^a-zA-Z0-9_-]/g, '') || '')}`} target="_blank" rel="noopener noreferrer" className="relative">
              {(() => {
                const safeAvatar = (seller.avatar || '').replace(/[<>\"'&]/g, '');
                if (safeAvatar && safeAvatar.startsWith('data:image')) {
                  return (
                    <div className="relative">
                      <img src={safeAvatar} alt="Seller" className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600 shadow-lg" />
                      {/* Online Status Dot */}
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                        sellerOnline 
                          ? 'bg-green-500 border-white dark:border-gray-800 shadow-lg animate-pulse' 
                          : 'bg-gray-400 border-white dark:border-gray-800'
                      }`}>
                        <div className={`w-full h-full rounded-full ${
                          sellerOnline ? 'bg-green-400' : 'bg-gray-300'
                        }`}></div>
                      </div>
                    </div>
                  );
                } else if (safeAvatar && safeAvatar.startsWith('http')) {
                  return (
                    <div className="relative">
                      <img src={safeAvatar} alt="Seller" className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600 shadow-lg" />
                      {/* Online Status Dot */}
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                        sellerOnline 
                          ? 'bg-green-500 border-white dark:border-gray-800 shadow-lg animate-pulse' 
                          : 'bg-gray-400 border-white dark:border-gray-800'
                      }`}>
                        <div className={`w-full h-full rounded-full ${
                          sellerOnline ? 'bg-green-400' : 'bg-gray-300'
                        }`}></div>
                      </div>
                    </div>
                  );
                } else {
                  // Modern avatar using ui-avatars.com (same as ChatPanel header)
                  const avatarName = seller.username || 'Seller';
                  const modernAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(avatarName)}&background=0f0f0f&color=ffffff&rounded=true&size=64`;
                  
                  return (
                    <div className="relative">
                      <img 
                        src={modernAvatar} 
                        alt="Seller" 
                        className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-lg" 
                      />
                      {/* Online Status Dot */}
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                        sellerOnline 
                          ? 'bg-green-500 border-white dark:border-gray-800 shadow-lg animate-pulse' 
                          : 'bg-gray-400 border-white dark:border-gray-800'
                      }`}>
                        <div className={`w-full h-full rounded-full ${
                          sellerOnline ? 'bg-green-400' : 'bg-gray-300'
                        }`}></div>
                      </div>
                    </div>
                  );
                }
              })()}
            </a>
            <div>
              <div className={`flex items-center gap-2 font-bold text-lg transition-colors duration-300 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                <a href={`/profile/${encodeURIComponent(seller.username?.replace(/[^a-zA-Z0-9_-]/g, '') || '')}`} target="_blank" rel="noopener noreferrer" className="hover:underline">{(() => {
                  const safeUsername = (seller.username || '').replace(/[<>\"'&]/g, '');
                  return safeUsername || 'Unknown Seller';
                })()}</a>
                {seller.verified && (
                  <button
                    onClick={() => setShowVerificationPopup(true)}
                    className="inline-block align-middle hover:scale-110 transition-transform duration-200"
                    title="Verified Seller - Click for details"
                  >
                    {/* Theme-aware tick SVG */}
                    <svg className="inline-block align-middle" width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill={isDarkMode ? "#6B7280" : "#2563eb"}/><path d="M6 10.5l3 3 5-5" stroke={isDarkMode ? "#1F2937" : "#ffffff"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                )}
              </div>
              <button className={`mt-2 text-white font-semibold rounded-lg px-5 py-2 text-base shadow transition flex items-center justify-center gap-2 ${
                isDarkMode 
                  ? 'bg-[#034d3f] hover:bg-[#023a2f]' 
                  : 'bg-green-500 hover:bg-green-600'
              }`} onClick={handleContactSeller} disabled={contactLoading || !sellerDetails}>
                {contactLoading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                {contactLoading ? 'Starting chat...' : 'Contact Seller'}
              </button>
              {!sellerDetails && (
                <div className="text-red-500 text-sm mt-2">Seller details not loaded.</div>
              )}
            </div>
          </div>
                      {/* Ratings summary card - Modern Design with Better Light Mode */}
            <div className="mb-6">
              <div className={`rounded-2xl shadow-lg px-7 py-6 text-center border transition-all duration-300 ${
                isDarkMode 
                  ? 'bg-[#1a1a1a] border border-gray-700/50 shadow-xl shadow-black/30' 
                  : 'bg-white border-gray-200 shadow-xl shadow-gray-200/50 hover:shadow-2xl hover:shadow-gray-300/50'
              }`}>
                <div className={`font-bold text-lg mb-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Seller's Ratings</div>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className={`text-4xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{(sellerReviewStats.averageRating || 0).toFixed(1)}</span>
                  <span className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-6 h-6" fill={i < Math.round(sellerReviewStats.averageRating || 0) ? '#facc15' : '#e5e7eb'} viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z"/></svg>
                    ))}
                  </span>
                </div>
                <div className={`text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Based on <a href="#" className="text-blue-500 underline font-semibold hover:text-blue-600 transition-colors duration-300">{sellerReviewStats.totalReviews || 0} reviews</a></div>
              </div>
            </div>
          {/* Seller Profile Details - Modern Card Design */}
          <div className={`font-semibold mb-4 mt-6 text-lg transition-colors duration-300 flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></div>
            <span>Seller Profile Details</span>
          </div>
          
          {/* Profile Stats Cards */}
          <div className="space-y-3">
            {/* Joined Date Card */}
            <div className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg ${
              isDarkMode 
                ? 'bg-gray-800/50 border border-gray-700/50 hover:bg-gray-800/70 shadow-gray-900/50' 
                : 'bg-white border border-gray-200 hover:bg-gray-50 shadow-gray-200/50 hover:shadow-gray-300/50'
            }`}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-300 ${
                isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
              }`}>
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className={`text-xs font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Joined Date</div>
                <div className={`font-semibold transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>{(() => {
                  const raw = seller.joined;
                  if (!raw) return 'N/A';
                  const date = new Date(raw);
                  if (isNaN(date.getTime())) {
                    const safe = String(raw || '').replace(/[<>\"'&]/g, '');
                    return safe || 'N/A';
                  }
                  return date.toLocaleDateString();
                })()}</div>
              </div>
            </div>

            {/* Deals & Revenue Card */}
            <div className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg ${
              isDarkMode 
                ? 'bg-gray-800/50 border border-gray-700/50 hover:bg-gray-800/70 shadow-gray-900/50' 
                : 'bg-white border border-gray-200 hover:bg-gray-50 shadow-gray-200/50 hover:shadow-gray-300/50'
            }`}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-300 ${
                isDarkMode ? 'bg-green-500/20' : 'bg-green-100'
              }`}>
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="flex-1">
                <div className={`text-xs font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Total Deals & Revenue</div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                  <span className={`font-semibold transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>{(() => {
                    const safeDeals = (seller.deals || 0).toString().replace(/[<>\"'&]/g, '');
                    return safeDeals || '0';
                  })()} deals</span>
                  <span className={`text-xs transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>worth</span>
                  <span className={`font-bold text-green-500 transition-colors duration-300`}>
                    ${(() => {
                      const safeTotal = (seller.total || 0).toString().replace(/[<>\"'&]/g, '');
                      return Number(safeTotal).toLocaleString(undefined, {minimumFractionDigits:0, maximumFractionDigits:0});
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Related Products Section - Consistent with other pages */}
      <div className="mx-auto px-3 sm:px-4 mt-8 sm:mt-12 mb-6 sm:mb-8" style={{ maxWidth: '1495px' }}>
        {/* Section Header - Consistent with Home page */}
        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6 w-full">
          <h2 className={`text-xl sm:text-2xl md:text-[30px] font-bold whitespace-nowrap mr-3 sm:mr-4 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>Related Products</h2>
          <div className="flex-1 h-1 bg-gradient-to-r from-red-600 via-red-400 to-transparent rounded-full" />
          {/* Navigation Controls - Mobile Optimized */}
          <div className="flex gap-2">
            <button
              className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-full border shadow transition disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode 
                  ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' 
                  : 'border-gray-200 bg-white hover:bg-gray-100'
              }`}
              onClick={() => setRelatedPage((prev) => Math.max(prev - 1, 0))}
              disabled={relatedPage === 0}
            >
              {/* Left Arrow */}
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              className={`w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-full border shadow transition disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode 
                  ? 'border-gray-600 bg-gray-700 hover:bg-gray-600' 
                  : 'border-gray-200 bg-white hover:bg-gray-100'
              }`}
              onClick={() => setRelatedPage((prev) => Math.min(prev + 1, totalPages - 1))}
              disabled={relatedPage >= totalPages - 1}
            >
              {/* Right Arrow */}
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Products Grid - Responsive Layout - Consistent with other pages */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3 lg:gap-6 max-w-[1495px] mx-auto items-stretch">
          {relatedProductsLoading ? (
            // Show skeleton loading cards
            [...Array(PRODUCTS_PER_PAGE)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-full flex flex-col transform transition-all duration-200">
                <ProductCardSkeleton />
              </div>
            ))
          ) : (
            // Show actual products
            relatedProducts
              .slice(relatedPage * PRODUCTS_PER_PAGE, (relatedPage + 1) * PRODUCTS_PER_PAGE)
              .map((p, i) => (
                <div key={p._id || i + relatedPage * PRODUCTS_PER_PAGE} className="h-full flex flex-col transform transition-all duration-200 hover:scale-[1.02] hover:shadow-lg">
                  <ProductCard
                    product={p}
                    showBoostedBadge={true}
                    showBoostedBar={false}
                    reserveStatsSpace={true}
                  />
                </div>
              ))
          )}
        </div>
        
        {/* Mobile Pagination Dots */}
        <div className="flex justify-center mt-6 sm:hidden">
          <div className="flex gap-2">
            {relatedProductsLoading ? (
              // Show skeleton dots when loading
              [...Array(3)].map((_, idx) => (
                <div
                  key={`dot-skeleton-${idx}`}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                  }`}
                />
              ))
            ) : (
              // Show actual pagination dots
              [...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setRelatedPage(idx)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    relatedPage === idx ? 'bg-red-500' : 'bg-gray-300'
                  }`}
                />
              ))
            )}
          </div>
        </div>
      </div>
      {/* Modal for image viewing */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
          <button
            onClick={closeModal}
            className="absolute top-6 right-8 flex items-center justify-center w-12 h-12 rounded-xl border border-white/60 bg-gray-700/70 hover:bg-gray-700/90 transition shadow-lg focus:outline-none"
            aria-label="Close"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="8" y1="8" x2="20" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              <line x1="20" y1="8" x2="8" y2="20" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
          <button
            onClick={prevImage}
            className="absolute left-6 top-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg hover:bg-gray-200 transition border border-gray-200"
            aria-label="Previous"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polyline points="17 7 11 14 17 21" stroke="#222" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </button>
          <img src={sanitizeImageSrc(allImages[modalIndex])} alt="Product Large" className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl border-4 border-white" />
          <button
            onClick={nextImage}
            className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg hover:bg-gray-200 transition border border-gray-200"
            aria-label="Next"
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polyline points="11 7 17 14 11 21" stroke="#222" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </button>
        </div>
      )}
      
      {/* Verification Popup */}
      {showVerificationPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`relative w-full max-w-md sm:max-w-lg lg:max-w-xl mx-auto rounded-2xl shadow-2xl transition-all duration-300 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 border border-gray-700/50' 
              : 'bg-white border border-gray-200'
          }`}>
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 sm:p-5 lg:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <div>
                  <h3 className={`text-lg sm:text-xl font-bold transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Verified Seller</h3>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Identity Confirmed</p>
                </div>
              </div>
              <button
                onClick={() => setShowVerificationPopup(false)}
                className={`p-2 rounded-full transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-5 lg:p-6">
              <div className={`text-sm sm:text-base leading-relaxed transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <div className={`p-3 sm:p-4 rounded-xl border transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-800/50 border-gray-700/50' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="mb-2">
                    <span className={`text-sm font-semibold transition-colors duration-300 ${
                      isDarkMode ? 'text-green-400' : 'text-green-600'
                    }`}>Verification Complete</span>
                  </div>
                  <p className={`text-xs sm:text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Seller has provided their ID details and a selfie video to help us confirm their identity. We may share this information with law enforcement if fraudulent activity occurs.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-4 sm:p-5 lg:p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowVerificationPopup(false)}
                className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg font-semibold transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600 hover:border-gray-500'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 hover:border-gray-400'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
      
      {/* Chat App Popup */}
      <ChatAppPopup
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        conversationId={selectedConversationId}
      />
      
      {/* Product View Footer Bar - Mobile Only */}
      {product && (
        <ProductViewFooterBar
          product={product}
          onContactSeller={handleContactSeller}
          onBuyNow={() => navigate('/checkout', { state: { product } })}
          contactLoading={contactLoading}
          isInStock={(!('stockStatus' in product) || !('quantity' in product) || (product.stockStatus === 'instock' && product.quantity > 0))}
        />
      )}

      {/* Add styles for zoom and overlay icon */}
      <style>{`
        .zoom-image-container {
          position: relative;
          display: inline-block;
          overflow: hidden;
          border-radius: 1rem;
        }
        .zoom-image {
          transition: transform 0.3s cubic-bezier(.4,2,.6,1);
          will-change: transform;
          display: block;
        }
        .zoom-image-container:hover .zoom-image {
          transform: scale(1.08);
        }
        .zoom-icon-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s;
          z-index: 2;
        }
        .zoom-image-container:hover .zoom-icon-overlay {
          opacity: 1;
        }
      `}</style>
    </div>
  );
}

export default ProductView; 